// src/components/FileReducer/ProcessingOptions.tsx

import React from 'react';
import { ArrowLeft, Shield, Zap, Sparkles, Check, Image as ImageIcon } from 'lucide-react';
import { formatFileSize } from '../../utils/formatUtils';

interface OptionDefinition {
    label: string;
    type: 'range' | 'button-group' | 'checkbox';
    description?: string;
    min?: number;
    max?: number;
    step?: number;
    options?: Array<{ label: string; value: any; description?: string }>;
    value?: any;
}

interface ProcessingOptionsProps {
    file: File;
    options: Record<string, OptionDefinition>;
    values: Record<string, any>;
    onChange: (newValues: Record<string, any>) => void;
    onProcess: () => void;
    onBack: () => void;
    loading: boolean;
    processButtonText?: string;
}

const ProcessingOptions: React.FC<ProcessingOptionsProps> = ({
    file,
    options,
    values,
    onChange,
    onProcess,
    onBack,
    loading,
    processButtonText = "Process File"
}) => {
    const handleChange = (name: string, value: any) => {
        onChange({
            ...values,
            [name]: value
        });
    };

    const renderOption = (name: string, option: OptionDefinition) => {
        const { label, type, description } = option;

        switch (type) {
            case 'range':
                return (
                    <div className="flex flex-col gap-2" key={name}>
                        <div className="flex justify-between items-center">
                            <label className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</label>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-bold dark:bg-blue-900/30 dark:text-blue-400">{values[name]}</span>
                        </div>
                        {description && <p className="text-[13px] text-slate-500 m-0 mb-1 dark:text-slate-400">{description}</p>}
                        <input
                            type="range"
                            min={option.min || 0}
                            max={option.max || 100}
                            step={option.step || 1}
                            value={values[name] || option.value}
                            onChange={(e) => handleChange(name, parseInt(e.target.value))}
                            className="w-full h-1 bg-slate-200 rounded outline-none my-3 appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110 dark:bg-slate-700 dark:[&::-webkit-slider-thumb]:border-slate-800"
                        />
                    </div>
                );

            case 'button-group':
                return (
                    <div className="flex flex-col gap-2" key={name}>
                        <label className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</label>
                        {description && <p className="text-[13px] text-slate-500 m-0 mb-1 dark:text-slate-400">{description}</p>}
                        <div className="flex bg-slate-100 p-1 rounded-lg gap-1 dark:bg-slate-800">
                            {option.options?.map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`flex-1 flex items-center justify-center gap-2 p-2.5 border-none bg-transparent text-[13px] font-semibold rounded-md cursor-pointer transition-all ${values[name] === opt.value ? 'bg-white text-primary shadow-sm dark:bg-slate-700 dark:text-blue-400' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                                    onClick={() => handleChange(name, opt.value)}
                                    title={opt.description}
                                    type="button"
                                >
                                    {values[name] === opt.value && <Check size={14} />}
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 'checkbox':
                const isPro = name === 'removeMetadata' || name === 'autoResize';
                return (
                    <div
                        className={`flex justify-between items-center p-4 border rounded-xl bg-white transition-all cursor-pointer dark:bg-slate-800 ${values[name] ? 'bg-blue-50 border-primary shadow-[0_0_0_1px_var(--color-primary)] dark:bg-blue-900/10 dark:border-primary' : 'border-slate-200 hover:bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:hover:bg-slate-800/80 dark:hover:border-slate-600'}`}
                        key={name}
                        onClick={() => handleChange(name, !(values[name] ?? option.value))}
                    >
                        <div className="flex-1">
                            <div className="flex items-center gap-2.5">
                                <div className={`w-[18px] h-[18px] border-2 rounded-sm flex items-center justify-center transition-colors ${values[name] ? 'bg-primary border-primary' : 'border-slate-300 dark:border-slate-600'}`}>
                                    {values[name] && <Check size={12} className="text-white" strokeWidth={3} />}
                                </div>
                                <span className="text-[15px] font-semibold text-slate-800 flex items-center gap-2 dark:text-slate-200">
                                    {label}
                                    {isPro && <span className="inline-flex items-center gap-1 py-0.5 px-1.5 bg-gradient-to-br from-blue-600 to-indigo-500 text-white rounded text-[10px] font-bold tracking-wider">
                                        <Sparkles size={10} /> PRO
                                    </span>}
                                </span>
                            </div>
                            {description && <p className="text-[13px] text-slate-500 mt-1 mb-0 ml-7 dark:text-slate-400">{description}</p>}
                        </div>
                        <div className={`transition-colors ${values[name] ? 'text-primary' : 'text-slate-400 dark:text-slate-500'}`}>
                            {name === 'removeMetadata' ? <Shield size={20} /> : <Zap size={20} />}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="w-full">
            <div className="bg-white border border-slate-200 rounded-2xl p-0 overflow-hidden shadow-sm mb-6 dark:bg-slate-800 dark:border-slate-700">
                <div className="p-0">
                    {/* File summary bar */}
                    <div className="flex justify-between items-center px-6 py-4 bg-slate-50 border-b border-slate-200 dark:bg-slate-800/50 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <ImageIcon size={20} className="text-primary" />
                            <div className="flex flex-col">
                                <span className="text-[15px] font-medium text-slate-800 max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-200">{file.name}</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(file.size)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold dark:bg-blue-900/30 dark:text-blue-400">
                            <Sparkles size={14} />
                            AI Ready
                        </div>
                    </div>

                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {Object.entries(options).map(([name, option]) =>
                            renderOption(name, option)
                        )}
                    </div>
                </div>
            </div>

            <div className="flex justify-between items-center mt-8 gap-4 max-md:flex-col max-md:mt-6">
                <button
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent text-slate-600 border border-slate-300 rounded-lg font-medium cursor-pointer transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:opacity-70 disabled:cursor-not-allowed dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 max-md:w-full"
                    onClick={onBack}
                    disabled={loading}
                    type="button"
                >
                    <ArrowLeft size={18} />
                    Change File
                </button>

                <button
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white border-none rounded-full font-medium cursor-pointer transition-all min-w-[180px] hover:bg-blue-700 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex-1 max-w-[320px] h-12 text-base max-md:w-full max-md:max-w-none"
                    onClick={onProcess}
                    disabled={loading}
                    type="button"
                >
                    {loading ? (
                        <div className="flex items-center justify-center gap-1">
                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                        </div>
                    ) : (
                        <>
                            <Zap size={20} />
                            {processButtonText}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ProcessingOptions;
